// Cadre de l'application : rail de navigation bleu nuit (comme les maquettes), bandeau, zone de travail.
import { useEffect, useRef, type ReactNode } from 'react';
import { useEkera } from '../contexte';
import { effacerComplements } from '../domaine/complementsLocaux';
import { oublierHistorique } from '../domaine/console';
import { lienVers, PAGES, type Page } from '../routeur';

export function Cadre({
  page,
  titre: titreFourni,
  entreprise,
  cartes,
  surChangerCarte,
  children,
}: {
  page: Page;
  /** Titre de l'écran quand il dépend de l'onglet ou de la fiche ouverte (sinon celui de la page). */
  titre?: string;
  entreprise: string;
  cartes: { id: string; entreprise: string }[];
  surChangerCarte: (id: string) => void;
  children: ReactNode;
}) {
  const { identite, cid, ecritureAutorisee, moi, sante, motifLectureSeule, administrateur, demonstration } = useEkera();
  // « Collecteurs » : administration seulement (les autres rôles n'ont pas l'entrée de menu)
  const pages = PAGES.filter((p) => !['collecteurs', 'abonnement'].includes(p.id) || administrateur);
  const titre = titreFourni ?? PAGES.find((p) => p.id === page)?.titre ?? '';
  const h1 = useRef<HTMLHeadingElement>(null);
  const premier = useRef(true);
  useEffect(() => {
    document.title = `${titre} · Ekera`;
    // Changement d'écran : le focus va au titre, pour les lecteurs d'écran et la navigation au clavier.
    if (premier.current) premier.current = false;
    else h1.current?.focus();
  }, [titre]);
  const u = identite.utilisateur();
  const role = libelleRole(moi.roles);
  return (
    <div className="app">
      <a className="evitement" href="#contenu">
        Aller au contenu
      </a>
      <nav className="rail" aria-label="Navigation principale">
        <div>
          <div className="marque">
            <svg viewBox="0 0 100 100" width="28" height="28" aria-hidden="true">
              <path d="M76 14 H26 V86 H76 M26 50 H52" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <circle cx="76" cy="14" r="7.5" fill="currentColor" />
              <circle cx="26" cy="14" r="7.5" fill="currentColor" />
              <circle cx="26" cy="50" r="7.5" fill="currentColor" />
              <circle cx="26" cy="86" r="7.5" fill="currentColor" />
              <circle cx="76" cy="86" r="7.5" fill="currentColor" />
              <circle cx="62" cy="50" r="9" fill="#F0A35E" />
            </svg>
            <b>ekera</b>
          </div>
          <div className="client">{entreprise} · espace du DSI</div>
        </div>
        <div>
          <p className="railtete">Sommaire</p>
          <ol className="nav">
            {pages.map((p, i) => (
              <li key={p.id}>
                <a href={lienVers(p.id)} aria-current={p.id === page ? 'page' : undefined}>
                  <span className="n" aria-hidden="true">
                    {i + 1}
                  </span>
                  {p.libelle}
                </a>
              </li>
            ))}
          </ol>
        </div>
        <div className="railpied">
          {cartes.length > 1 && (
            <label>
              <span className="sr">Carte affichée</span>
              {/* Le nom complet de la carte, sur plusieurs lignes si besoin, lisible sans survol ; le sélecteur reste compact. */}
              <span className="carte-nom" aria-hidden="true" data-testid="carte-nom-complet">
                {libelleCarte(cartes.find((c) => c.id === cid) ?? cartes[0]!, cartes)}
              </span>
              <select value={cid} onChange={(e) => surChangerCarte(e.target.value)}>
                {cartes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {/* Nom lisible seulement : l'identifiant technique ne s'affiche pas (il reste dans l'adresse de l'application). */}
                    {libelleCarte(c, cartes)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <span data-testid="utilisateur">{u?.nom ?? moi.nom ?? 'Non connecté'}</span>
          <span className="aide" data-testid="role">
            Rôle : {role}
          </span>
          <span className="aide">
            {identite.mode === 'developpement'
              ? 'Mode développement : jeton fixe ajouté par le relais de développement.'
              : 'Connecté par OpenID Connect (fournisseur d’identité de l’entreprise).'}
          </span>
          {identite.mode === 'oidc' && (
            <button
              type="button"
              className="btn petit"
              onClick={() => {
                effacerComplements();
                oublierHistorique();
                void identite.deconnecter();
              }}
            >
              Se déconnecter
            </button>
          )}
        </div>
      </nav>
      <main id="contenu" tabIndex={-1}>
        <div className="bandeau">
          <span data-testid="entreprise-affichee">
            {entreprise}
          </span>
          <span className="ligne">
            {!ecritureAutorisee && (
              <span className="pastille" title={motifLectureSeule} data-testid="pastille-lecture-seule">
                Lecture seule
              </span>
            )}
            {demonstration && <span className="pastille" data-testid="pastille-donnees-fictives">Entreprise et données fictives</span>}
          </span>
        </div>
        {(sante.authentification === 'oidc-http' || moi.anonyme) && (
          <p className="bandeau-demo" role="note" data-testid="bandeau-demonstration">
            {moi.anonyme ? (
              <>
                <b>Mode développement, appel anonyme</b> : l’API ne connaît pas la personne ; aucune validation d’écriture ne peut être émise.
              </>
            ) : (
              <>
                <b>Démonstration</b> : le fournisseur d’identité est joint en <span className="num">http://</span> sur le réseau interne de la
                pile. Entreprise et personnes fictives ; à ne pas utiliser avec des données réelles.
              </>
            )}
          </p>
        )}
        <h1 ref={h1} tabIndex={-1}>
          {titre}
        </h1>
        {children}
      </main>
    </div>
  );
}

/** Nom lisible d'une carte ; deux cartes de la même entreprise se distinguent par un rang (« carte 2 »), jamais par un identifiant. */
function libelleCarte(c: { id: string; entreprise: string }, cartes: { id: string; entreprise: string }[]): string {
  const memes = cartes.filter((x) => x.entreprise === c.entreprise);
  return memes.length > 1 ? `${c.entreprise} (carte ${memes.findIndex((x) => x.id === c.id) + 1})` : c.entreprise;
}

/** Libellé du rôle le plus élevé (les rôles d'Ekera sont cumulatifs). */
export function libelleRole(roles: readonly string[]): string {
  const base = roles.includes('administration')
    ? 'administration (écriture, annulation, journal d’accès)'
    : roles.includes('ecriture')
      ? 'écriture (corrections et décisions du DSI)'
      : roles.includes('lecture')
        ? 'lecture seule'
        : 'aucun';
  // « partage » est à part (O18) : il n'est inclus dans aucun autre rôle et n'en inclut aucun, sauf la lecture.
  return roles.includes('partage') ? `${base} + partage hors de l’entreprise` : base;
}
